import { Controller } from "@hotwired/stimulus";
import { inverseSuggestionsFor } from "../lib/relationship_inverses";
import { scrollSheetToTop } from "../utils/sheet_scroll";

// Suggestion chips. The preset tags are server-rendered with the same classes,
// so the two rows read as one control — keep them in step.
const CHIP_CLASS = "border rounded-lg px-2 py-1 text-xs text-gray-700";
const CHIP_SELECTED = ["border-primary", "bg-primary/10"];

// Drives the "Add relationship" sheet on the neighbor profile: person/pet
// kind toggle, neighbor search-or-create, the in-household switch, and the
// HMIS role fields. Search mirrors preferred_partner_picker_controller —
// fetches server-rendered cards and injects them.
export default class extends Controller {
  static targets = [
    "kindField",
    "personSection",
    "petSection",
    "personTab",
    "petTab",
    "searchStep",
    "searchInput",
    "results",
    "createPrompt",
    "createStep",
    "firstNameField",
    "lastNameField",
    "subjectStep",
    "subjectCard",
    "subjectIdField",
    "configSection",
    "inHouseholdToggle",
    "hmisSection",
    "looseSection",
    "looseInput",
    "tagButton",
    "inverseInput",
    "inverseSuggestWrap",
    "inverseSuggestList",
    "mirrorPreview",
    "mirrorMineTag",
    "mirrorTheirsTag",
    "mirrorTheirsLabel",
    "connectionQuestion",
    "householdCardTitle",
    "looseCardTitle",
    "step",
    "suggestions",
    "suggestionList",
    "idleState",
    "searchHint",
    "suggestionSkeletons",
    "title",
    "back",
    "primary",
    "primaryLabel",
  ];
  static values = {
    searchUrl: String,
    theirTemplate: String,
    connectionQuestionTemplate: String,
    householdOptionTemplate: String,
    looseOptionTemplate: String,
    subjectFallback: String,
    panelUrl: String,
    step: { type: Number, default: 1 },
    // How long the skeletons hold before the real suggestions take their place.
    revealDelay: { type: Number, default: 850 },
  };

  declare readonly kindFieldTarget: HTMLInputElement;
  declare readonly personSectionTarget: HTMLElement;
  declare readonly petSectionTarget: HTMLElement;
  declare readonly personTabTarget: HTMLButtonElement;
  declare readonly petTabTarget: HTMLButtonElement;
  declare readonly searchStepTarget: HTMLElement;
  declare readonly searchInputTarget: HTMLInputElement;
  declare readonly resultsTarget: HTMLElement;
  declare readonly createPromptTarget: HTMLElement;
  declare readonly hasCreatePromptTarget: boolean;
  declare readonly createStepTarget: HTMLElement;
  declare readonly firstNameFieldTarget: HTMLInputElement;
  declare readonly lastNameFieldTarget: HTMLInputElement;
  declare readonly subjectStepTarget: HTMLElement;
  declare readonly subjectCardTarget: HTMLElement;
  declare readonly subjectIdFieldTarget: HTMLInputElement;
  declare readonly configSectionTarget: HTMLElement;
  declare readonly inHouseholdToggleTarget: HTMLInputElement;
  declare readonly hmisSectionTarget: HTMLElement;
  declare readonly looseSectionTarget: HTMLElement;
  declare readonly looseInputTarget: HTMLInputElement;
  declare readonly tagButtonTargets: HTMLElement[];
  declare readonly inverseInputTarget: HTMLInputElement;
  declare readonly inverseSuggestWrapTarget: HTMLElement;
  declare readonly inverseSuggestListTarget: HTMLElement;
  declare readonly mirrorPreviewTarget: HTMLElement;
  declare readonly mirrorMineTagTarget: HTMLElement;
  declare readonly mirrorTheirsTagTarget: HTMLElement;
  declare readonly mirrorTheirsLabelTarget: HTMLElement;
  declare readonly connectionQuestionTarget: HTMLElement;
  declare readonly hasConnectionQuestionTarget: boolean;
  declare readonly householdCardTitleTarget: HTMLElement;
  declare readonly looseCardTitleTarget: HTMLElement;
  declare readonly stepTargets: HTMLElement[];
  declare readonly suggestionsTarget: HTMLElement;
  declare readonly hasSuggestionsTarget: boolean;
  declare readonly suggestionListTarget: HTMLElement;
  declare readonly suggestionSkeletonsTarget: HTMLElement;
  declare readonly hasSuggestionSkeletonsTarget: boolean;
  declare readonly idleStateTarget: HTMLElement;
  declare readonly hasIdleStateTarget: boolean;
  declare readonly searchHintTarget: HTMLElement;
  declare readonly hasSearchHintTarget: boolean;
  declare readonly titleTarget: HTMLElement;
  declare readonly hasTitleTarget: boolean;
  declare readonly backTarget: HTMLButtonElement;
  declare readonly primaryTarget: HTMLButtonElement;
  declare readonly primaryLabelTarget: HTMLElement;
  declare readonly searchUrlValue: string;
  declare readonly theirTemplateValue: string;
  declare readonly connectionQuestionTemplateValue: string;
  declare readonly householdOptionTemplateValue: string;
  declare readonly looseOptionTemplateValue: string;
  declare readonly subjectFallbackValue: string;
  declare readonly panelUrlValue: string;
  declare stepValue: number;
  declare readonly revealDelayValue: number;

  private searchTimeout?: ReturnType<typeof setTimeout>;
  private revealTimeout?: ReturnType<typeof setTimeout>;
  private subjectFirstName = "";
  // Set from the picked search row: only a subject who lives with someone else,
  // or owns a pet, gets the merge step. The 80% case stays two taps.
  private needsMerge = false;

  connect() {
    this.adoptServerRenderedSubject();
    this.syncHouseholdSections();
    this.renderInverseUi();
    this.renderStep();
    this.revealSuggestions();
  }

  // "Add to household…" on a loose relationship row arrives with the subject
  // already chosen, so #select never runs. Without this the gate would stay
  // closed and the form would post straight into AddMember's refusal.
  private adoptServerRenderedSubject() {
    const card = this.subjectCardTarget.querySelector<HTMLElement>("[data-neighbor-id]");
    if (!card) return;

    this.needsMerge = card.dataset.needsMerge === "true";
    this.subjectFirstName = (card.dataset.neighborName ?? "").split(/\s+/)[0] ?? "";
  }

  disconnect() {
    clearTimeout(this.searchTimeout);
    clearTimeout(this.revealTimeout);
  }

  // The suggestions are already computed server-side; the skeletons are theatre.
  // Swapping them out is what starts each row's `.rise` — a display:none element
  // holds its animation at frame 0, so the delays only begin here.
  private revealSuggestions() {
    if (!this.hasSuggestionSkeletonsTarget) return;

    this.revealTimeout = setTimeout(() => {
      this.suggestionSkeletonsTarget.remove();
      this.suggestionListTarget.classList.remove("hidden");
    }, this.revealDelayValue);
  }

  setPersonKind() {
    this.setKind("person");
  }

  setPetKind() {
    this.setKind("pet");
  }

  private setKind(kind: string) {
    this.kindFieldTarget.value = kind;
    const person = kind === "person";
    this.personSectionTarget.classList.toggle("hidden", !person);
    this.petSectionTarget.classList.toggle("hidden", person);
    this.personTabTarget.toggleAttribute("data-active", person);
    this.petTabTarget.toggleAttribute("data-active", !person);
    this.syncRoleRequired();
  }

  // The cards name the person being added, who is picked (or typed) after the
  // sheet renders. Falls back to the placeholder rather than leaving a gap while
  // a brand-new neighbor is still unnamed.
  private syncConnectionCopy() {
    if (!this.hasConnectionQuestionTarget) return;

    const name =
      this.subjectFirstName ||
      this.firstNameFieldTarget.value.trim() ||
      this.subjectFallbackValue;

    const fill = (template: string) => template.replace("{name}", name);
    this.connectionQuestionTarget.textContent = fill(this.connectionQuestionTemplateValue);
    this.householdCardTitleTarget.textContent = fill(this.householdOptionTemplateValue);
    this.looseCardTitleTarget.textContent = fill(this.looseOptionTemplateValue);
  }

  // The role is only required while it is genuinely on screen. `hidden` is
  // display:none, and a required control the browser cannot focus makes
  // requestSubmit a silent no-op: no submit event, no validation bubble, and a
  // sheet that has stopped saving with nothing on screen to fix.
  //
  // Four separate wrappers can hide it — the pet tab, the "lives somewhere else"
  // card, changing the subject, and advancing to the merge step — and they all
  // hide by toggling this one class, so walking the chain covers every one of
  // them, including any added later.
  private syncRoleRequired() {
    const role = this.hmisSectionTarget.querySelector<HTMLSelectElement>("select");
    if (!role) return;

    role.required = this.isOnScreen(role);
  }

  private isOnScreen(element: HTMLElement) {
    for (let node: HTMLElement | null = element; node; node = node.parentElement) {
      if (node.classList.contains("hidden")) return false;
      if (node === this.element) break;
    }
    return true;
  }

  onSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.doSearch(value), 100);
  }

  private async doSearch(query: string) {
    // The create-a-new-neighbor prompt only appears once a search has been
    // attempted, and hides again when the query is cleared.
    const attempted = query.trim().length >= 2;
    if (this.hasCreatePromptTarget) {
      this.createPromptTarget.classList.toggle("hidden", !attempted);
    }
    // Suggestions are the idle state. Once staff are searching, the results are
    // the only answer that matters, so the two never show together.
    if (this.hasIdleStateTarget) this.idleStateTarget.classList.toggle("hidden", attempted);
    this.resultsTarget.classList.toggle("hidden", !attempted);
    if (!attempted) {
      this.resultsTarget.innerHTML = "";
      return;
    }
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);
    const response = await fetch(url.toString(), {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
    });
    if (!response.ok) return;
    this.resultsTarget.innerHTML = await response.text();
  }

  // Dismissing one suggestion must not also pick the person underneath it, so
  // the click stops here rather than bubbling to the container's #select.
  dismissSuggestion(event: Event) {
    event.stopPropagation();
    (event.currentTarget as HTMLElement).closest("[data-suggestion]")?.remove();
    // An empty card is just noise; the plain hint takes its place.
    if (this.hasSuggestionsTarget && this.suggestionListTarget.children.length === 0) {
      this.suggestionsTarget.classList.add("hidden");
      if (this.hasSearchHintTarget) this.searchHintTarget.classList.remove("hidden");
    }
  }

  // Collapses the list without removing the header, so it can be reopened.
  // Delegated from the results container: a card click bubbles up here.
  select(event: Event) {
    const card = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-neighbor-id]",
    );
    if (!card?.dataset.neighborId) return;

    this.subjectIdFieldTarget.value = card.dataset.neighborId;
    this.subjectFirstName = (card.dataset.neighborName ?? "").split(/\s+/)[0] ?? "";
    this.subjectCardTarget.innerHTML = card.outerHTML;
    this.needsMerge = card.dataset.needsMerge === "true";
    // A suggestion drawn from an emergency contact already knows how the two are
    // related ("sister"); carry that into the relationship field.
    const label = card.dataset.suggestedLabel?.trim();
    if (label && !this.looseInputTarget.value) {
      this.looseInputTarget.value = label;
      this.prefillInverse();
      this.renderInverseUi();
    }
    this.searchStepTarget.classList.add("hidden");
    this.createStepTarget.classList.add("hidden");
    this.subjectStepTarget.classList.remove("hidden");
    this.configSectionTarget.classList.remove("hidden");
    this.syncConnectionCopy();
    this.renderStep();
    this.syncRoleRequired();
    scrollSheetToTop(this.element);
  }

  startCreate() {
    this.subjectIdFieldTarget.value = "";
    this.prefillNameFromQuery();
    // A brand-new neighbor has no household to resolve.
    this.needsMerge = false;
    this.searchStepTarget.classList.add("hidden");
    this.createStepTarget.classList.remove("hidden");
    this.configSectionTarget.classList.remove("hidden");
    this.syncConnectionCopy();
    this.renderStep();
    this.syncRoleRequired();
    scrollSheetToTop(this.element);
  }


  // "Gilbert Garcia" searched but not found → first/last name prefilled.
  private prefillNameFromQuery() {
    const query = this.searchInputTarget.value.trim();
    if (!query) return;

    const [first, ...rest] = query.split(/\s+/);
    this.firstNameFieldTarget.value = first;
    this.lastNameFieldTarget.value = rest.join(" ");
    this.subjectFirstName = first;
    this.syncMirror();
  }

  // Back to searching, from either the create fields or the selected pill.
  changeSubject() {
    this.subjectIdFieldTarget.value = "";
    this.needsMerge = false;
    this.createStepTarget.classList.add("hidden");
    this.subjectStepTarget.classList.add("hidden");
    this.configSectionTarget.classList.add("hidden");
    this.searchStepTarget.classList.remove("hidden");
    this.stepValue = 1;
    this.renderStep();
    this.syncRoleRequired();
    scrollSheetToTop(this.element);
  }

  syncHouseholdSections() {
    const inHousehold = this.inHouseholdToggleTarget.checked;
    this.hmisSectionTarget.classList.toggle("hidden", !inHousehold);
    this.looseSectionTarget.classList.toggle("hidden", inHousehold);
    this.syncRoleRequired();
    // Turning the switch off drops the merge step back out of the flow: a
    // loose tie moves nobody between households.
    this.renderStep();
  }

  // ---- Step machine -------------------------------------------------------
  //
  // The merge step only exists for a household move, so the last step is
  // computed rather than fixed. The panel spells out the household as it will
  // stand, so it is the readback — nothing follows it.

  private get lastStep(): number {
    return this.needsMerge && this.inHouseholdToggleTarget.checked ? 2 : 1;
  }

  primary() {
    if (this.stepValue >= this.lastStep) {
      this.submit();
      return;
    }
    this.stepValue += 1;
    if (this.stepValue === 2) this.loadFrame("household_merge_frame", this.panelUrlValue);
    this.renderStep();
    scrollSheetToTop(this.element);
  }

  back() {
    if (this.stepValue <= 1) return;

    this.stepValue -= 1;
    this.renderStep();
    scrollSheetToTop(this.element);
  }

  // requestSubmit is a no-op when constraint validation fails, and it fires no
  // event to say so, so the disable has to be undone by hand — otherwise a
  // blocked submit leaves a dead button and no way back.
  private submit() {
    const form = this.element as HTMLFormElement;
    let submitted = false;
    const markSubmitted = () => {
      submitted = true;
    };

    form.addEventListener("submit", markSubmitted);
    this.primaryTarget.disabled = true;
    try {
      form.requestSubmit();
    } finally {
      form.removeEventListener("submit", markSubmitted);
      if (!submitted) this.primaryTarget.disabled = false;
    }
  }

  // A 422 re-renders the sheet, but a failed submit that stays put must not
  // leave the button dead.
  onSubmitEnd(event: CustomEvent) {
    if (!event.detail?.success) this.primaryTarget.disabled = false;
  }

  private renderStep() {
    const step = Math.min(this.stepValue, this.lastStep);
    this.stepValue = step;

    this.stepTargets.forEach((el) =>
      el.classList.toggle("hidden", el.dataset.step !== String(step)),
    );
    const active = this.stepTargets.find((el) => el.dataset.step === String(step));
    if (active) {
      if (this.hasTitleTarget) this.titleTarget.textContent = active.dataset.stepTitle ?? "";
      // A step that still has steps after it advances rather than saves, so it
      // must not promise "Save".
      const advancing = step < this.lastStep && active.dataset.stepCtaNext;
      this.primaryLabelTarget.textContent = advancing || active.dataset.stepCta || "";
    }
    this.backTarget.classList.toggle("hidden", step <= 1);
    // The step container wraps the role select, so moving off step 1 takes it
    // off screen.
    this.syncRoleRequired();
  }

  private loadFrame(frameId: string, url: string) {
    const frame = this.element.querySelector<HTMLElement>(`turbo-frame#${frameId}`);
    if (!frame || !url) return;

    const target = new URL(url, window.location.origin);
    target.searchParams.set("subject_neighbor_id", this.subjectIdFieldTarget.value);
    frame.setAttribute("src", target.toString());
  }

  pickTag(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    this.looseInputTarget.value = button.dataset.tag ?? button.innerText.trim();
    this.prefillInverse();
    this.renderInverseUi();
  }

  onLooseTagInput() {
    this.prefillInverse();
    this.renderInverseUi();
  }

  onNewFirstName(event: Event) {
    this.subjectFirstName = (event.target as HTMLInputElement).value.trim().split(/\s+/)[0] ?? "";
    this.syncConnectionCopy();
    this.syncMirror();
  }

  // A chip is selected when the field already holds its label, so typing the
  // same word by hand lights it up too and picking a second tag releases the
  // first. Driven off the inputs rather than click state, which would drift the
  // moment staff edited the field.
  private syncChipSelection() {
    const mark = (button: HTMLElement, on: boolean) =>
      CHIP_SELECTED.forEach((klass) => button.classList.toggle(klass, on));
    const label = (button: HTMLElement) =>
      (button.dataset.tag ?? button.textContent ?? "").trim().toLowerCase();

    const mine = this.looseInputTarget.value.trim().toLowerCase();
    this.tagButtonTargets.forEach((button) => mark(button, label(button) === mine));

    const theirs = this.inverseInputTarget.value.trim().toLowerCase();
    this.inverseSuggestListTarget
      .querySelectorAll<HTMLElement>("button")
      .forEach((button) => mark(button, label(button) === theirs));
  }

  syncMirror() {
    const mine = this.looseInputTarget.value.trim();
    const theirs = this.inverseInputTarget.value.trim();
    this.mirrorPreviewTarget.classList.toggle("hidden", !(mine && theirs));
    this.mirrorMineTagTarget.textContent = mine;
    this.mirrorTheirsTagTarget.textContent = theirs;
    if (this.subjectFirstName && this.theirTemplateValue) {
      this.mirrorTheirsLabelTarget.textContent = this.theirTemplateValue.replace("{name}", this.subjectFirstName);
    }
    this.syncChipSelection();
  }

  // Auto-fill the inverse from the suggestion map ("Baby mama" -> "Baby daddy").
  private prefillInverse() {
    const suggestion = inverseSuggestionsFor(this.looseInputTarget.value)[0];
    if (suggestion) this.inverseInputTarget.value = suggestion;
  }

  private renderInverseUi() {
    const suggestions = inverseSuggestionsFor(this.looseInputTarget.value);
    this.inverseSuggestWrapTarget.classList.toggle("hidden", suggestions.length === 0);
    this.inverseSuggestListTarget.replaceChildren(
      ...suggestions.map((label) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = CHIP_CLASS;
        button.textContent = label;
        button.addEventListener("click", () => {
          this.inverseInputTarget.value = label;
          this.syncMirror();
        });
        return button;
      }),
    );
    this.syncMirror();
  }
}
